import { Component, ReactNode } from "react";
import { logError } from "@/lib/errorTelemetry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, retryCount: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logError(
      "runtime_crash",
      error.message,
      info.componentStack?.split("\n")[1]?.trim() || "ErrorBoundary",
      error.stack
    );

    // Auto-retry once after 1.5s
    if (this.state.retryCount < 1) {
      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          retryCount: prev.retryCount + 1,
        }));
      }, 1500);
    }
  }

  render() {
    if (this.state.hasError && this.state.retryCount >= 1) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-8">
          <div className="text-center space-y-2">
            <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              We've logged this issue automatically. Try refreshing the page.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    if (this.state.hasError) {
      // Show brief loading state during auto-retry
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      );
    }

    return this.props.children;
  }
}
